
import { FC, createContext, useState, ReactNode, useEffect, useCallback } from 'react';
import Cookies from 'universal-cookie';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseApp';
import { registerPushDevice, unregisterPushDevice } from '../pushDevice';
import {
  clearAuthSession,
  fetchUserMeProfile,
  getApiHost,
  getAuthHeaders,
  getAuthValidatePath,
  isValidAuthToken,
  loadStoredAuthToken,
} from './Common';

const saferCookies = new Cookies();
window.saferGoogleToken = loadStoredAuthToken();

const storedScheme = window.saferGoogleToken
  ? saferCookies.get('saferAuthScheme')
  : undefined;

if (window.saferGoogleToken && storedScheme === 'Google') {
  const isSecure = window.location.protocol === 'https:';
  const cookieOptions = {
    sameSite: (isSecure ? 'none' : 'lax') as 'none' | 'lax',
    secure: isSecure,
    path: '/',
    maxAge: 3600,
  };
  saferCookies.set('saferAuthScheme', 'Firebase', cookieOptions);
}

window.saferAuthScheme = window.saferGoogleToken
  ? (saferCookies.get('saferAuthScheme') || 'Firebase')
  : undefined;

export const defaultUser = {
  profile: undefined,
  loaded: false,
  handleLogin: (response: any) => {},
  handleLogout: () => {},
}

export const UserContext = createContext(defaultUser);

const handleLoginProfile = (response, setProfile) => {
  if (response === undefined) {
    clearAuthSession();
    window.saferApiHost = undefined;
    window.saferUserOrg = undefined;
    setProfile(undefined);
  } else if (!isValidAuthToken(response.tokenId)) {
    clearAuthSession();
    setProfile(undefined);
  } else {
    window.saferLogoutInProgress = false;
    window.saferGoogleToken = response.tokenId;
    window.saferAuthScheme = response.authScheme || 'Firebase';
    window.saferUserOrg = response.profileObj?.org;
    const isSecure = window.location.protocol === 'https:';
    const cookieOptions = {
      sameSite: (isSecure ? 'none' : 'lax') as 'none' | 'lax',
      secure: isSecure,
      path: '/',
      maxAge: 3600
    };
    saferCookies.set('saferGoogleToken', window.saferGoogleToken, cookieOptions);
    saferCookies.set('saferAuthScheme', window.saferAuthScheme, cookieOptions);
    setProfile(response.profileObj);
  }
}

const useAuthRestoration = (setProfile, setLoaded) => {
  useEffect(() => {
    const restoreAuth = async () => {
      const fallbackTimeout = setTimeout(() => {
        setLoaded(true);
      }, 5000);

      if (window.saferGoogleToken && !window.saferLogoutInProgress) {
        try {
          getApiHost((apiHost) => {
            const apiUrl = `${apiHost}${getAuthValidatePath()}`;
            fetch(apiUrl, { headers: getAuthHeaders() })
              .then(response => response.json())
              .then(authResponse => {
                clearTimeout(fallbackTimeout);
                if (authResponse.status === 'Valid') {
                  fetchUserMeProfile(apiHost, authResponse)
                    .then((profileObj) => {
                      handleLoginProfile({
                        tokenId: window.saferGoogleToken,
                        authScheme: window.saferAuthScheme,
                        profileObj,
                      }, setProfile);
                      registerPushDevice(apiHost).catch((err) => {
                        console.warn('Push device registration failed:', err);
                      });
                      setTimeout(() => {
                        if (window.saferGoogleToken) {
                          handleLoginProfile(undefined, setProfile);
                        }
                      }, 3600 * 1000);
                      setLoaded(true);
                    });
                } else {
                  clearAuthSession();
                  setProfile(undefined);
                  setLoaded(true);
                }
              })
              .catch(error => {
                console.error('Failed to validate token:', error);
                clearTimeout(fallbackTimeout);
                clearAuthSession();
                setProfile(undefined);
                setLoaded(true);
              });
          });
        } catch (error) {
          console.error('Failed to restore authentication:', error);
          clearTimeout(fallbackTimeout);
          clearAuthSession();
          setProfile(undefined);
          setLoaded(true);
        }
      } else {
        clearTimeout(fallbackTimeout);
        setLoaded(true);
      }
    };

    restoreAuth();
  }, [setProfile, setLoaded]);
};

export const LoginContext: FC<{ children?: ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState(undefined);
  const [loaded, setLoaded] = useState(false);

  useAuthRestoration(setProfile, setLoaded);

  const handleLogin = response => {
    handleLoginProfile(response, setProfile);
    if (response) {
      setTimeout(() => {
        if (window.saferGoogleToken === response.tokenId) {
          handleLoginProfile(undefined, setProfile);
        }
      }, 3600 * 1000);
    }
  };

  const handleLogout = useCallback(async () => {
    window.saferLogoutInProgress = true;
    getApiHost((apiHost) => {
      unregisterPushDevice(apiHost).catch(() => {});
    });
    await signOut(auth).catch(() => {});
    handleLoginProfile(undefined, setProfile);
    setTimeout(() => {
      window.saferLogoutInProgress = false;
    }, 1000);
  }, [setProfile]);

  return (
    <UserContext.Provider value={{profile, loaded, handleLogin, handleLogout}}>
      { children }
    </UserContext.Provider>
  );
}

export const FAKE_LOGIN_RESPONSE = {
  tokenId: 'fakeToken',
  authScheme: 'Firebase',
  profileObj: {
    givenName: 'fakeName',
    email: 'dev@safereaction.com',
    org: 'master',
  },
};

export const FakeLoginContext: FC<{ children?: ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.saferGoogleToken === 'fakeToken' && !window.saferLogoutInProgress) {
      handleLoginProfile(FAKE_LOGIN_RESPONSE, setProfile);
    }
    setLoaded(true);
  }, []);

  const handleLogin = (response) => {
    handleLoginProfile(response, setProfile);
    setLoaded(true);
  };
  const handleLogout = () => {
    handleLoginProfile(undefined, setProfile);
    setLoaded(true);
  };
  return (
    <UserContext.Provider value={{profile, loaded, handleLogin, handleLogout}}>
      { children }
    </UserContext.Provider>
  );
}
